import { Box, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import { Material } from "../schemas";
import { compareAsc, format, parseISO } from "date-fns";
import { getDateFnsLocale } from "../date-utils";
import { useState } from "react";

import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

const locale = getDateFnsLocale();

type SortOrder = 'asc' | 'desc'
type SortKey = ['name' | 'start' | 'end', SortOrder];

export function Detail(props: { name: string, data: Material }) {

  const [sortKeys, setSortKeys] = useState<SortKey[]>([['name', 'asc'], ['start', 'asc'], ['end', 'asc']]);

  props.data.scenes.sort((a, b) => {
    let v = 0;
    for (let [key, order] of sortKeys) {
      switch (key) {
        case 'name':
          v = a.scene_name.localeCompare(b.scene_name);
          break;
        case 'start':
          v = compareAsc(parseISO(a.start_datetime), parseISO(b.start_datetime));
          break;
        case 'end':
          v = compareAsc(parseISO(a.end_datetime), parseISO(b.end_datetime));
          break;
      }
      if (order === 'desc') v *= -1;
      if (v !== 0) return v;
    }
    return 0;
  });

  const [topKey, topOrder] = sortKeys[0];

  return (
    <Box>
      <Typography variant="h5">{props.name}</Typography>
      <TableContainer>
        <Table size="small">
          <TableBody>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>
                Start at
              </TableCell>
              <TableCell>
                <Typography variant="inherit">{format(parseISO(props.data.datetime), 'PPpp', { locale })}</Typography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>
                Executed by
              </TableCell>
              <TableCell>
                {props.data.experiencer_name
                  ? <Typography variant="inherit">{props.data.experiencer_name}</Typography>
                  : <Typography variant="inherit" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>(no name)</Typography>
                }
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, verticalAlign: 'top' }}>
                Scenes
              </TableCell>
              <TableCell>
                <TableContainer sx={{ backgroundColor: 'action.hover', p: 1 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <Typography component="span" variant="body2" sx={{ pr: 1 }}>Name</Typography>
                          <SortKeyButton
                            order={topKey === 'name' ? topOrder : null}
                            onClick={order => setSortKeys([['name', order], ['start', order], ['end', order]])}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography component="span" variant="body2" sx={{ pr: 1 }}>Start</Typography>
                          <SortKeyButton
                            order={topKey === 'start' ? topOrder : null}
                            onClick={order => setSortKeys([['start', order], ['end', order], ['name', order]])}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography component="span" variant="body2" sx={{ pr: 1 }}>End</Typography>
                          <SortKeyButton
                            order={topKey === 'end' ? topOrder : null}
                            onClick={order => setSortKeys([['end', order], ['start', order], ['name', order]])}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography component="span" variant="body2">Score</Typography>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {props.data.scenes.map((s, i) => {
                        const numerator = s.clicked_area_count + s.correct_question_count;
                        const denominator = s.total_area_count + s.total_question_count;
                        return <TableRow key={i}>
                          <TableCell>{s.scene_name}</TableCell>
                          <TableCell>{format(parseISO(s.start_datetime), 'PPpp', { locale })}</TableCell>
                          <TableCell>{format(parseISO(s.end_datetime), 'PPpp', { locale })}</TableCell>
                          <TableCell>{numerator}&ensp;/&ensp;{denominator}</TableCell>
                        </TableRow>
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}

const SortKeyButton = (props: { order?: SortOrder | null, onClick: (order: SortOrder) => void }) => {

  return (
    <IconButton
      size="small"
      onClick={() => props.onClick(props.order === 'asc' ? 'desc' : 'asc')}
    >
      {props.order === 'asc' && <ArrowUpwardIcon fontSize="small" />}
      {props.order === 'desc' && <ArrowDownwardIcon fontSize="small" />}
      {!props.order && <ArrowUpwardIcon fontSize="small" color="disabled" />}
    </IconButton>
  )
}
